import React from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import MediaQuery from "react-responsive";
import Expander from "../../../../../components/Expander";
import Citations from "../../../../../components/Citations";
import { StyledChartsGroup } from "../../../../../common/styles/ChartsStyles";
import AgeChart from "../../../../../components/atoms/charts/AgeChart";
import Chart from "../../../../../components/atoms/charts/Chart";
import withErrorGuard from "../../../../../HOC/withErrorGuard";
import {
  getTp53GermlineSchema,
  getTp53GermlineItems,
} from "../../../../../state/selectors/Tp53.selectors";
import ComponentPanel from "../../../../../components/atoms/ComponentPanel/ComponentPanel";
import HelperText from "../../../../../components/atoms/HelperText";

const getSummary = (data) => {
  return (
    <React.Fragment>
      {data.total_samples && (
        <React.Fragment>
          <h5> Summary of results </h5>
          <h6>
            <b>{"Total samples: "}</b>
            {data.total_samples
              ? data.total_samples.toLocaleString().replace(",", " ")
              : ""}
          </h6>
        </React.Fragment>
      )}
      {data.hotspot != null && (
        <h6>
          <b>{"Hotspot: "}</b>
          {data.hotspot ? "true" : "false"}
        </h6>
      )}
      {data.effect && (
        <h6>
          <b>{"Effect: "}</b>
          {data.effect}
        </h6>
      )}
      {data.unaffected != null && (
        <h6>
          <b>{"Unaffected: "}</b>
          {data.unaffected ? "true" : "false"}
        </h6>
      )}
    </React.Fragment>
  );
};

const getCharts = (data) => {
  const {
    age_freq: age,
    age_at_diagnosis: ageDiagnosis,
    cancer_name: cancerName,
    cancer_type: cancerType,
    country,
    generation,
    germline_carrier: germlineCarrier,
    inheritance,
    oncotree_code: oncotreeCode,
    os_status: osStatus,
    sex,
    tissue_type: tissueType,
  } = data;

  return (
    <StyledChartsGroup>
      <AgeChart showEmptyState={false} data={age} title="Age frequency" />
      <AgeChart
        showEmptyState={false}
        data={ageDiagnosis}
        title="Age at initial pathological diagnosis"
      />
      <Chart showEmptyState={false} data={cancerType} title="Cancer type" />
      <Chart showEmptyState={false} data={cancerName} title="Cancer name" />
      <Chart showEmptyState={false} data={country} title="Country" />
      <Chart showEmptyState={false} data={generation} title="Generation" />
      <Chart
        showEmptyState={false}
        data={germlineCarrier}
        title="Germline carrier"
      />
      <Chart showEmptyState={false} data={inheritance} title="Inheritance" />
      <Chart showEmptyState={false} data={oncotreeCode} title="Oncotree code" />
      <Chart showEmptyState={false} data={osStatus} title="Vital status" />
      <Chart showEmptyState={false} data={sex} title="Sex" />
      <Chart showEmptyState={false} data={tissueType} title="Tissue type" />
    </StyledChartsGroup>
  );
};

const IARCTP53Germline = ({ schema, values }) => {
  const title = "IARC TP53 Germline";
  const url = "https://tp53.isb-cgc.org/";
  let tmpl = null;
  let version = null;

  if (schema && values) {
    tmpl = values.map((item, i) => {
      let labels;
      if (item.items) {
        // eslint-disable-next-line react/prop-types
        labels = schema.items.properties.items.items.properties;
      }
      version = item.version;
      return (
        <div key={i}>
          <div className="varsome-inner-container">
            <MediaQuery minWidth={768}>
              {item.items ? (
                <table className="varsome-table">
                  <thead>
                    <tr>
                      <th>{labels.age_at_diagnosis.label}</th>
                      <th>{labels.country.label}</th>
                      <th>{labels.familycase.label}</th>
                      <th>{labels.familycase_group.label}</th>
                      <th>{labels.family_code.label}</th>
                      <th>{labels.generation.label}</th>
                      <th>{labels.morphology.label}</th>
                      <th>{labels.topography.label}</th>
                      <th>{labels.sex.label}</th>
                    </tr>
                  </thead>
                  <Expander showMore="4" component="tbody">
                    {item.items.map((elem, k) => {
                      return (
                        <tr key={`${i}-${k}`}>
                          <td>{elem.age_at_diagnosis || "-"}</td>
                          <td>{elem.country || "-"}</td>
                          <td>{elem.familycase || "-"}</td>
                          <td>{elem.familycase_group || "-"}</td>
                          <td>{elem.family_code || "-"}</td>
                          <td>{elem.generation || "-"}</td>
                          <td>{elem.morphology || "-"}</td>
                          <td>
                            {(elem.topography || "-") +
                              (elem.unaffected === 1 ? " (unaffected)" : "")}
                          </td>
                          <td>{elem.sex || "-"}</td>
                        </tr>
                      );
                    })}
                  </Expander>
                </table>
              ) : null}
            </MediaQuery>
            <MediaQuery maxWidth={767}>
              {item.items ? (
                <Expander showMore="4">
                  {item.items.map((elem, j) => {
                    return (
                      <dl
                        className="dl-horizontal varsome-dl-horizontal"
                        key={j}
                      >
                        <dt className="col-3 col-xs-3">
                          {labels.age_at_diagnosis.label}
                        </dt>
                        <dd className="col-9 col-xs-9">
                          {elem.age_at_diagnosis || "-"}
                        </dd>
                        <dt className="col-3 col-xs-3">
                          {labels.country.label}
                        </dt>
                        <dd className="col-9 col-xs-9">
                          {elem.country || "-"}
                        </dd>
                        <dt className="col-3 col-xs-3">
                          {labels.effect.label}
                        </dt>
                        <dd className="col-9 col-xs-9">{elem.effect || "-"}</dd>
                        <dt className="col-3 col-xs-3">
                          {labels.familycase.label}
                        </dt>
                        <dd className="col-9 col-xs-9">
                          {elem.familycase || "-"}
                        </dd>
                        <dt className="col-3 col-xs-3">
                          {labels.familycase_group.label}
                        </dt>
                        <dd className="col-9 col-xs-9">
                          {elem.familycase_group || "-"}
                        </dd>
                        <dt className="col-3 col-xs-3">
                          {labels.family_code.label}
                        </dt>
                        <dd className="col-9 col-xs-9">
                          {elem.family_code || "-"}
                        </dd>
                        <dt className="col-3 col-xs-3">
                          {labels.generation.label}
                        </dt>
                        <dd className="col-9 col-xs-9">
                          {elem.generation || "-"}
                        </dd>
                        <dt className="col-3 col-xs-3">
                          {labels.morphology.label}
                        </dt>
                        <dd className="col-9 col-xs-9">
                          {elem.morphology || "-"}
                        </dd>
                        <dt className="col-3 col-xs-3">{labels.sex.label}</dt>
                        <dd className="col-9 col-xs-9">{elem.sex || "-"}</dd>
                        <dt className="col-3 col-xs-3">
                          {labels.topography.label}
                        </dt>
                        <dd className="col-9 col-xs-9">
                          {(elem.topography || "-") +
                            (elem.unaffected === 1 ? " (unaffected)" : "")}
                        </dd>
                      </dl>
                    );
                  })}
                </Expander>
              ) : null}
            </MediaQuery>
            {item.items && (
              <Citations>
                <p>
                  Bouaoun L, Sonkin D, Ardin M, Hollstein M, Byrnes G, Zavadil
                  J, Olivier M. TP53 Variations in Human Cancers: New Lessons
                  from the IARC TP53 Database and Genomics Data. Hum Mutat. 2016
                  Sep;37(9):865-76
                </p>
              </Citations>
            )}
            {getSummary(item)}
            {getCharts(item)}
          </div>
        </div>
      );
    });
  }

  return (
    <div className="varsome-result">
      <div className="varsome-container varsome-container__basic">
        <ComponentPanel
          title={title}
          version={version}
          url={url}
          helperPopup={<HelperText fieldDescriptionKey={title} />}
          content={tmpl}
        />
      </div>
    </div>
  );
};

IARCTP53Germline.propTypes = {
  schema: PropTypes.shape({
    items: PropTypes.shape({
      properties: PropTypes.shape({}),
    }),
  }),
  values: PropTypes.arrayOf(PropTypes.shape({})),
};

IARCTP53Germline.defaultProps = {
  schema: null,
  values: null,
};

const mapStateToProps = (state) => ({
  schema: getTp53GermlineSchema(state),
  values: getTp53GermlineItems(state),
});

export default connect(
  mapStateToProps,
  {}
)(withErrorGuard(IARCTP53Germline, "IARC TP53 Germline", "IARC TP53 Germline"));
